// kfe-play-progress.js — per-item play-count progress indicator.
//
// Brief 099 §5. Shown on COMPLETABLE lesson-item rows that are not yet
// finished: a circular arc (fills clockwise as `completed` approaches
// `required`) wrapping a ▶ play glyph, with an "X/Y" count beneath. Mirrors
// the iOS lesson-chart play-count canon (Lesson 160 screenshot: HAAI "0/1",
// FaceFonics "0/3" / "1/3").
//
// When `completed >= required` the lesson-detail row swaps THIS for a ✓ tick
// (Brief 099 §5 "completed state"); this component renders the in-progress
// state only and clamps defensively if handed completed >= required anyway.
//
// Hand-rolled SVG (CLAUDE.md two-runtime-deps rule + the squiggle / report
// chart precedent). Same stroke-dasharray arc technique as
// kfe-circular-progress.js, sized down for an inline row badge.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

export class KFEPlayProgress extends KFEComponent {
  static props = ["completed", "required"];
  static styles = `
    :host {
      display: inline-flex; flex-direction: column; align-items: center;
      gap: 2px; flex: 0 0 auto;
    }
    .ring { position: relative; width: 30px; height: 30px; }
    svg { width: 100%; height: 100%; transform: rotate(-90deg); display: block; }
    .track { stroke: var(--kfe-border); fill: none; stroke-width: 3; }
    .bar { stroke: var(--kfe-accent); fill: none; stroke-width: 3; stroke-linecap: round; }
    /* Play glyph, centred in the ring. Counter-rotates the svg's -90deg. */
    .play {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      color: var(--kfe-accent); font-size: 0.7rem; line-height: 1;
    }
    .count {
      font-size: 0.7rem; font-weight: 600; color: var(--kfe-muted);
      white-space: nowrap;
    }
  `;

  render() {
    const required = Math.max(1, Math.floor(Number(/** @type {any} */ (this).required) || 1));
    const doneRaw = Math.floor(Number(/** @type {any} */ (this).completed) || 0);
    const done = Math.max(0, Math.min(required, doneRaw));
    const frac = done / required;

    const r = 13, c = 2 * Math.PI * r;
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 30 30");
    const track = document.createElementNS(ns, "circle");
    track.setAttribute("class", "track");
    track.setAttribute("cx", "15"); track.setAttribute("cy", "15"); track.setAttribute("r", String(r));
    const bar = document.createElementNS(ns, "circle");
    bar.setAttribute("class", "bar");
    bar.setAttribute("cx", "15"); bar.setAttribute("cy", "15"); bar.setAttribute("r", String(r));
    bar.setAttribute("stroke-dasharray", `${c * frac} ${c}`);
    svg.appendChild(track);
    svg.appendChild(bar);

    return html`
      <div class="ring" role="img" aria-label="${done} / ${required}">
        ${svg}
        <span class="play" aria-hidden="true">▶</span>
      </div>
      <span class="count">${done}/${required}</span>
    `;
  }
}
